import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import Layout from "@/components/Layout";
import Countdown from "@/components/Countdown";
import { useAuth } from "@/lib/auth";
import { Gavel, Trophy, Calendar, ArrowRight, Coins } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [auctions, setAuctions] = useState([]);
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [a, e, m] = await Promise.all([
        api.get("/auctions"),
        api.get("/events"),
        api.get("/users"),
      ]);
      setAuctions(a.data);
      setEvents(e.data);
      setMembers(m.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const open = auctions.filter(a => a.status === "open");
  const closed = auctions.filter(a => a.status === "closed");

  return (
    <Layout>
      <div className="mb-8">
        <h1 className="font-fredoka text-4xl sm:text-5xl font-bold">
          Hi {user?.name?.split(" ")[0]} <span className="inline-block animate-wiggle">👋</span>
        </h1>
        <p className="mt-2 text-gray-700">Bid on what&apos;s next, or host something great to earn more points.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        {/* Stats */}
        <div className="md:col-span-4 brut-card p-6 bg-sunny">
          <div className="flex items-center gap-2"><Coins strokeWidth={3} className="w-5 h-5"/><span className="font-bold">Your points</span></div>
          <div data-testid="my-points" className="font-fredoka text-6xl font-bold mt-2">{user?.points ?? 0}</div>
          <div className="mt-3 text-sm">Earned by hosting successful events.</div>
        </div>
        <div className="md:col-span-4 brut-card p-6 bg-mint">
          <div className="flex items-center gap-2"><Gavel strokeWidth={3} className="w-5 h-5"/><span className="font-bold">Live auctions</span></div>
          <div className="font-fredoka text-6xl font-bold mt-2">{open.length}</div>
          <div className="mt-3 text-sm">Sealed bids, second-price wins.</div>
        </div>
        <div className="md:col-span-4 brut-card p-6 bg-lilac">
          <div className="flex items-center gap-2"><Trophy strokeWidth={3} className="w-5 h-5"/><span className="font-bold">Family members</span></div>
          <div className="font-fredoka text-6xl font-bold mt-2">{members.length}</div>
          <div className="mt-3 text-sm">Bidding together, no arguments.</div>
        </div>

        {/* Auctions */}
        <div className="md:col-span-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-fredoka text-2xl font-bold">Live auctions</h2>
            <Link to="/members" className="text-sm underline">See leaderboard</Link>
          </div>
          {loading ? (
            <div className="brut-card p-6">Loading…</div>
          ) : open.length === 0 ? (
            <div className="brut-card p-8 text-center bg-white">
              <Gavel strokeWidth={2.5} className="w-10 h-10 mx-auto mb-3"/>
              <div className="font-fredoka text-xl font-bold">No live auctions yet</div>
              <p className="mt-2 text-sm text-gray-700">
                {user?.is_admin ? "Head to Admin and create one!" : "Ask an admin to create one."}
              </p>
            </div>
          ) : (
            <div className="grid gap-4">
              {open.map(a => (
                <AuctionCard key={a.auction_id} a={a} events={events} onExpire={load}/>
              ))}
            </div>
          )}

          {closed.length > 0 && (
            <>
              <h3 className="font-fredoka text-xl font-bold mt-8 mb-3">Recent results</h3>
              <div className="grid gap-3">
                {closed.slice(0, 5).map(a => {
                  const winner = members.find(m => m.user_id === a.winner_user_id);
                  return (
                    <Link key={a.auction_id} to={`/auctions/${a.auction_id}`} data-testid={`closed-auction-${a.auction_id}`}
                          className="brut-card p-4 bg-white flex items-center justify-between hover:bg-cream">
                      <div>
                        <div className="font-fredoka font-bold">{a.title}</div>
                        <div className="text-sm text-gray-700">
                          {winner ? `${winner.name} won • paid ${a.winning_price} pts` : "No bids"}
                        </div>
                      </div>
                      <ArrowRight strokeWidth={3} className="w-5 h-5"/>
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Sidebar: leaderboard + upcoming events */}
        <div className="md:col-span-4 grid gap-6">
          <div className="brut-card p-5 bg-white">
            <div className="flex items-center gap-2 mb-3"><Trophy strokeWidth={3} className="w-5 h-5"/><h3 className="font-fredoka text-lg font-bold">Leaderboard</h3></div>
            <ol className="space-y-2">
              {members.slice(0, 6).map((m, i) => (
                <li key={m.user_id} className="flex items-center justify-between border-2 border-black rounded-lg px-3 py-2 bg-cream">
                  <span className="flex items-center gap-2">
                    <span className="font-fredoka font-bold w-5">{i + 1}</span>
                    <span className="font-bold">{m.name}</span>
                    {m.is_admin && <span className="chip bg-peach px-2 py-0.5 text-[10px]">admin</span>}
                  </span>
                  <span className="font-fredoka font-bold">{m.points}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="brut-card p-5 bg-white">
            <div className="flex items-center gap-2 mb-3"><Calendar strokeWidth={3} className="w-5 h-5"/><h3 className="font-fredoka text-lg font-bold">Events</h3></div>
            {events.length === 0 ? (
              <div className="text-sm text-gray-700">No events yet.</div>
            ) : (
              <ul className="space-y-2">
                {events.slice(0, 5).map(e => (
                  <li key={e.event_id} className="border-2 border-black rounded-lg px-3 py-2 bg-cream">
                    <div className="font-bold">{e.title}</div>
                    <div className="text-xs text-gray-700">
                      {e.outcome === "successful" ? "🏆 Successful" : e.outcome === "unsuccessful" ? "😅 Fizzled" : "⏳ Upcoming"}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

function AuctionCard({ a, events, onExpire }) {
  const ev = events.find(e => e.event_id === a.event_id);
  const img = ev?.image_url;
  return (
    <Link to={`/auctions/${a.auction_id}`} data-testid={`auction-card-${a.auction_id}`}
      className="brut-card overflow-hidden hover:-translate-y-0.5 transition-transform block bg-white">
      <div className="grid grid-cols-1 sm:grid-cols-5">
        <div className="sm:col-span-2 bg-peach border-b-2 sm:border-b-0 sm:border-r-2 border-black">
          {img ? (
            <img src={img} alt="" className="w-full h-40 sm:h-full object-cover"/>
          ) : (
            <div className="w-full h-40 sm:h-full flex items-center justify-center">
              <Gavel strokeWidth={3} className="w-12 h-12"/>
            </div>
          )}
        </div>
        <div className="sm:col-span-3 p-5">
          <div className="flex items-center gap-2 mb-2">
            <span className="chip bg-sunny">Live</span>
            <span className="chip"><Countdown target={a.closes_at} onExpire={onExpire} testId={`countdown-${a.auction_id}`}/></span>
          </div>
          <h3 className="font-fredoka text-2xl font-bold">{a.title}</h3>
          <p className="text-sm mt-1 line-clamp-2 text-gray-800">{a.description || ev?.description}</p>
          <div className="mt-4 inline-flex items-center gap-2 font-bold">
            Place a sealed bid <ArrowRight strokeWidth={3} className="w-4 h-4"/>
          </div>
        </div>
      </div>
    </Link>
  );
}
